/*#
# Dispatch: a poke when the owner has work waiting

An agent session that went idle doesn't hear that the owner moved work to
ready. The dispatcher closes that gap (board #161): a Claude session the
owner runs on `/loop` asks the board, each tick, which sessions to poke,
sends each one short message, and records it.

It is **off by default** and switched on per project, on the board, by a
person signed in: project settings › Dispatch names the session to poke,
how often (`dispatchEvery`, minutes, default 60) and how many times a day
(`dispatchCap`, default 4). Each poke starts a paid turn, so those are cost
controls. A token can turn dispatch off, never on.

A poke is due when all of these hold:
- a ready task is waiting that **a person** moved to ready (an agent's own
  move doesn't count, so a poke never approves on the owner's behalf), that
  isn't a question, and that nobody is on;
- it was moved there **since the last poke** (no nagging about the same
  work);
- the last poke is older than the interval, and today's are under the cap.

`planDispatch(store)` says, per project with dispatch on, whether a poke is
due and why not when it isn't; it reads only. `recordPoke(store, project)`
writes the poke as a comment on the task (`dispatch: poked …`), so every
poke is on the board: who was poked, about what, when.

```js
import { MemoryLog, VirtaStore, planDispatch, recordPoke } from 'tosijs-virta'

const store = new VirtaStore({ log: new MemoryLog(), identity: 'owner' })
await store.load()
await store.setProject('demo', { enrolled: 'today', dispatch: 'demo-session' })
await store.create({ title: 'port the tests', tags: ['project:demo', 'status:ready'] })
const [before] = planDispatch(store)
await recordPoke(store, 'demo')
const [after] = planDispatch(store)
preview.textContent = [before.due, before.text, after.due, after.why].join('\n')
```
```test
test('a poke is due once, then not until something new is ready', () => {
  const [due, text, again, why] = preview.textContent.split('\n')
  expect(due).toBe('true')
  expect(text).toContain('#1 "port the tests"')
  expect(again).toBe('false')
  expect(why).toBe('nothing new moved to ready since the last poke')
})
```
*/

/*{ "parent": "Reference", "order": 63 }*/

import type { Task, VirtaEvent } from './model'
import type { VirtaStore } from './store'
import { priorityRank, tagValues, workersOf } from './tags'

/** How a poke's comment starts: what `planDispatch` counts. */
export const POKE_PREFIX = 'dispatch: poked '

export const DEFAULT_EVERY_MINUTES = 60
export const DEFAULT_CAP = 4

export interface DispatchPlan {
  project: string
  /** the session to poke */
  session: string
  everyMinutes: number
  cap: number
  /** pokes recorded today (UTC) */
  today: number
  /** when the last poke was recorded, or '' */
  lastPokeAt: string
  /** the task a poke would name */
  task: { id: string; num: number; title: string } | null
  due: boolean
  /** why not, when it isn't due; what it's about when it is */
  why: string
  /** the message to send the session */
  text: string
}

/** Whether a person (not a token, not an agent) wrote this event. */
function byPerson(e: VirtaEvent): boolean {
  if (e.attribution) return !e.attribution.token
  return !e.by.includes(' × ')
}

/** A positive whole number from a setting, or the default. */
function count(value: string, fallback: number): number {
  const n = Number.parseInt(value, 10)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

/** Every project with dispatch on (or just `project`): due or not, and why. */
export function planDispatch(
  store: VirtaStore,
  options: { project?: string; now?: number } = {}
): DispatchPlan[] {
  const now = options.now ?? Date.now()
  const day = new Date(now).toISOString().slice(0, 10)
  return store.projects
    .filter(
      (p) =>
        p.dispatch.trim() !== '' &&
        p.enrolled !== '' &&
        (!options.project || p.name === options.project)
    )
    .map((p): DispatchPlan => {
      const everyMinutes = count(p.dispatchEvery, DEFAULT_EVERY_MINUTES)
      const cap = count(p.dispatchCap, DEFAULT_CAP)
      const all = store.query(`project:${p.name} is:any archived:any`)
      const pokes = all
        .flatMap((t) => store.events(t.id))
        .filter(
          (e) =>
            e.kind === 'commented' &&
            (e.payload as { text: string }).text.startsWith(POKE_PREFIX)
        )
        .sort((a, b) => a.seq - b.seq)
      const last = pokes[pokes.length - 1]
      const today = pokes.filter((e) => e.at.slice(0, 10) === day).length
      // ready work a person moved there: when, and the task
      const waiting = store
        .query(`project:${p.name} status:ready`)
        .filter(
          (t) =>
            !tagValues(t.tags, 'kind').includes('question') &&
            !workersOf(t.tags).length
        )
        .flatMap((t): { task: Task; seq: number; who: string }[] => {
          const moved = store
            .events(t.id)
            .filter(
              (e) =>
                (e.kind === 'tagged' &&
                  (e.payload as { tag: string }).tag === 'status:ready') ||
                (e.kind === 'created' &&
                  (e.payload as { tags: string[] }).tags.includes(
                    'status:ready'
                  ))
            )
            .pop()
          return moved && byPerson(moved)
            ? [{ task: t, seq: moved.seq, who: moved.by }]
            : []
        })
        .sort(
          (a, b) =>
            priorityRank(a.task.tags) - priorityRank(b.task.tags) ||
            a.task.num - b.task.num
        )
      const fresh = waiting.filter((w) => !last || w.seq > last.seq)
      const top = fresh[0] ?? waiting[0]
      const plan: DispatchPlan = {
        project: p.name,
        session: p.dispatch.trim(),
        everyMinutes,
        cap,
        today,
        lastPokeAt: last?.at ?? '',
        task: top
          ? { id: top.task.id, num: top.task.num, title: top.task.title }
          : null,
        due: false,
        why: '',
        text: top
          ? `virta dispatch: #${top.task.num} "${top.task.title}" is ready in ${p.name} (moved there by ${top.who}). If you're free, take it: \`virta show #${top.task.num} --all\`. If you're mid-task or waiting on the owner, carry on.`
          : '',
      }
      if (!waiting.length)
        plan.why = 'nothing a person moved to ready is waiting'
      else if (!fresh.length)
        plan.why = 'nothing new moved to ready since the last poke'
      else if (today >= cap) plan.why = `the cap: ${today} of ${cap} today`
      else if (last && now - Date.parse(last.at) < everyMinutes * 60_000)
        plan.why = `the interval: next after ${new Date(
          Date.parse(last.at) + everyMinutes * 60_000
        ).toISOString()}`
      else {
        plan.due = true
        plan.why = `#${top!.task.num} is ready`
      }
      return plan
    })
}

/**
 * Record a poke for `project`: a comment on the task it named, so the
 * board shows who was poked, about what, when. Refused (with the reason)
 * when no poke is due — a poke is recorded only for one the plan allowed.
 */
export async function recordPoke(
  store: VirtaStore,
  project: string,
  now?: number
): Promise<DispatchPlan> {
  const [plan] = planDispatch(store, { project, now })
  if (!plan) throw new Error(`dispatch is off for ${project}`)
  if (!plan.due || !plan.task)
    throw new Error(`no poke is due for ${project}: ${plan.why}`)
  await store.comment(
    plan.task.id,
    `${POKE_PREFIX}${plan.session} about this (${plan.today + 1} of ${
      plan.cap
    } today)`
  )
  return plan
}
